import React from 'react';
import styled from '@emotion/styled';
import Button from '@unicons-base/Button/Button';
import { Fonts, mediaQuery } from '@styles/index';
import { emptyObject } from '@constants/empty';
import { useRouter } from 'next/router';
import usePageHead from '@cont/PreSubscribed/usePageHead';

import { ICONS_GOAL_UID } from '@constants/unacademyIcons/icons.constants';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@api/selectors/subscription';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';

interface AnyfeedButtonProps {
  title: any;
  backgroundColor?: string | null;
  titleColors?: string[] | null;
  backgroundColors?: string[] | null;
  titleColor: string | null;
  isSubscriptionCta?: boolean;
  isCourseCta?: boolean;
  className?: string;
  ctaMetaData?: any;
}

type StyledButtonType = {
  $color: string | null;
  $backgroundColor: string | null;
  $titleColors: string[] | null;
  $backgroundColors: string[] | null;
};

const StyledButton = styled(Button)<StyledButtonType>`
  font-size: ${Fonts.H6};
  width: 234px;
  font-weight: ${Fonts.BOLD};
  color: ${({ $color, $titleColors }) =>
    $color || $titleColors
      ? $titleColors?.[0] || $color
      : 'var(--color-i-icon-dark-tertiary)'};
  background: ${({ $backgroundColor, $backgroundColors }) =>
    $backgroundColor || $backgroundColors
      ? `linear-gradient(261.69deg, ${$backgroundColors?.[0]} -0.19%, ${$backgroundColors?.[1]} 59.57%, ${$backgroundColors?.[2]} 85.24%)`
      : 'var(--color-i-icons-gradient)'};
  border-color: ${({ $backgroundColor }) =>
    $backgroundColor || 'var(--color-i-icon-gold)'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: auto;
    max-width: 328px;
  }
`;

const AnyfeedButton: React.FC<AnyfeedButtonProps> = ({
  title,
  titleColor,
  titleColors,
  backgroundColor,
  backgroundColors,
  isSubscriptionCta,
  isCourseCta,
  ctaMetaData,
  className
}) => {
  const router = useRouter();
  const { slug: courseSlug } = ctaMetaData || emptyObject;
  const { selectedGoal } = useSelectedGoal();
  const currentGoalUID = ICONS_GOAL_UID || selectedGoal.uid;
  const isIconsSubscribedPlus = useMyInfo(
    getIsSubscribedToPlus(currentGoalUID)
  );
  const isIconsSubscribedOffline = useMyInfo(
    getIsSubscribedToOffline(currentGoalUID)
  );

  const { handleSubscriptionClick } = usePageHead({
    goalSlug: selectedGoal.goalSlug,
    goalUID: currentGoalUID,
    goalDetails: selectedGoal,
    isSubscribed: false
  });

  const handleClick = () => {
    if (isCourseCta && courseSlug) {
      router.push(`/icons/courses/${courseSlug}`);
    }
    if (
      isSubscriptionCta &&
      !(isIconsSubscribedPlus || isIconsSubscribedOffline)
    ) {
      handleSubscriptionClick();
    }
  };

  return (
    <StyledButton
      onClick={handleClick}
      $color={titleColor}
      $backgroundColor={backgroundColor}
      $backgroundColors={backgroundColors}
      $titleColors={titleColors}
      className={className}
    >
      {title}
    </StyledButton>
  );
};

AnyfeedButton.displayName = 'AnyfeedButton';

AnyfeedButton.defaultProps = {
  isSubscriptionCta: true,
  isCourseCta: false
};

export default AnyfeedButton;
